export interface Location {
  lat: number;
  lng: number;
}

export interface Incentive {
  deviceLocationIncentiveRuleInfo: DeviceLocationIncentiveRuleInfo;
}

export interface DeviceLocationIncentiveRuleInfo {
  locationList: LocationRule[];
}

export interface LocationRule {
  latLng: {
    latitude: number;
    longitude: number;
  };
  radiusMeters: number;
}

export const DISTANCE_UNIT = {
  M: 'm',
  KM: 'km',
  MI: 'mi',
  FT: 'ft',
} as const;

export type DistanceUnits = typeof DISTANCE_UNIT;

export const getDistanceUnitForCountry = ({
  countryCode,
}: {
  countryCode: string;
}): DistanceUnits[keyof DistanceUnits] => {
  switch (countryCode) {
    case 'US':
    case 'GB':
      return DISTANCE_UNIT.MI;
    default:
      return DISTANCE_UNIT.KM;
  }
};

const deg2rad = (deg: number) => deg * (Math.PI / 180);

/**
 * Great-circle distance between two coordinates, in meters.
 */
export const getDistanceInMeters = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const earthRadiusM = 6371000;
  const dLat = deg2rad(lat2 - lat1);
  const dLng = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusM * c;
};

/**
 * Determine whether a device location satisfies the device-location geofence
 * rules of the selected incentives.
 *
 * Each incentive that defines a `deviceLocationIncentiveRuleInfo` is satisfied
 * when the device is within `radiusMeters` of at least one location in its
 * `locationList`. All such incentives must be satisfied.
 */
export const isInsideDeviceGeofence = (
  deviceLocation: Location | null,
  selectedIncentives: Incentive[],
): boolean => {
  const rules = (selectedIncentives ?? [])
    .map(incentive => incentive?.deviceLocationIncentiveRuleInfo)
    .filter(Boolean);

  // No geofence rules means there is no location restriction to satisfy.
  if (rules.length === 0) {
    return true;
  }

  // A geofence is required but we don't have the device location.
  if (!deviceLocation) {
    return false;
  }

  return rules.every(rule =>
    (rule.locationList ?? []).some(({ latLng, radiusMeters }) => {
      if (!latLng) {
        return false;
      }
      const distance = getDistanceInMeters(
        deviceLocation.lat,
        deviceLocation.lng,
        latLng.latitude,
        latLng.longitude,
      );
      return distance <= radiusMeters;
    }),
  );
};
